import { useRouter } from 'vue-router';
import type { RouteLocationRaw } from 'vue-router';
import type { RouteKey } from '@elegant-router/types';
import { router as globalRouter } from '@/router';

/**
 * Router push
 *
 * Jump to the specified route, it can replace function router.push
 *
 * @param inSetup Whether is in vue script setup
 */
export function useRouterPush(inSetup = true) {
  const router = inSetup ? useRouter() : globalRouter;
  const route = globalRouter.currentRoute;

  const routerPush = router.push;

  const routerBack = router.back;

  interface RouterPushOptions {
    query?: Record<string, string>;
    params?: Record<string, string>;
  }

  async function routerPushByKey(key: RouteKey, options?: RouterPushOptions) {
    const { query, params } = options || {};

    const routeLocation: RouteLocationRaw = {
      name: key
    };

    if (query) {
      routeLocation.query = query;
    }

    if (params) {
      routeLocation.params = params;
    }

    return routerPush(routeLocation);
  }

  async function toHome() {
    return routerPushByKey('root');
  }

  /**
   * Navigate to login page
   *
   * @param loginModule The login module
   * @param redirectUrl The redirect url, if not specified, it will be the current route fullPath
   */
  async function toLogin(loginModule?: UnionKey.LoginModule, redirectUrl?: string) {
    const module = loginModule || 'pwd-login';

    const options: RouterPushOptions = {
      params: {
        module
      }
    };

    const redirect = redirectUrl || route.value.fullPath;

    options.query = {
      redirect
    };

    return routerPushByKey('login', options);
  }

  /**
   * Toggle login module
   *
   * @param module
   */
  async function toggleLoginModule(module: UnionKey.LoginModule) {
    const query = route.value.query as Record<string, string>;

    return routerPushByKey('login', { query, params: { module } });
  }

  /** Redirect from login */
  async function redirectFromLogin() {
    const redirect = route.value.query?.redirect as string;

    if (redirect) {
      if (window.location.search) {
        window.location.href = `${window.location.origin}/#${redirect}`;
      } else {
        routerPush(redirect);
      }
    } else if (window.location.search) {
      window.location.href = `${window.location.origin}`;
    } else {
      toHome();
    }
  }

  /**
   * 跳转到通用表单页面
   *
   * @param dataId 数据ID
   * @param formConfigName 表单配置名
   * @param activeMenu 激活菜单
   */
  async function toGeneralFormPage(dataId: string, formConfigName: string, activeMenu?: RouteKey) {
    const options: RouterPushOptions = {
      query: {
        id: dataId
      },
      params: {
        config: formConfigName
      }
    };
    if (activeMenu && options.query) {
      options.query.activeMenu = activeMenu;
    }
    return routerPushByKey('general-form', options);
  }

  /** 跳转到iframe */
  async function toIframePage(fileUrl: string, fileName: string, activeMenu?: RouteKey) {
    const options: RouterPushOptions = {
      query: {
        fileName
      },
      params: {
        url: fileUrl
      }
    };
    if (activeMenu && options.query) {
      options.query.activeMenu = activeMenu;
    }
    return routerPushByKey('iframe-page', options);
  }

  return {
    routerPush,
    routerBack,
    routerPushByKey,
    toLogin,
    toggleLoginModule,
    toGeneralFormPage,
    redirectFromLogin,
    toIframePage
  };
}
